import type { FC } from "react";
import { usePresentationPlayButton } from "@/interface/WidgetMenu/lib/hooks/usePresentationPlayButton";
import type { WidgetOptions } from "@/shared/types/widgetOptions";
import type { MenuOptionComponentProps } from "../../types";
import { useAddTextButton } from "../hooks/useAddTextButton";
import { useAiAssistantButton } from "../hooks/useAiAssistantButton";
import { useAlignButton } from "../hooks/useAlignButton";
import { useArrowEndButton } from "../hooks/useArrowEndButton";
import { useBorderButton } from "../hooks/useBorderButton";
import { useCheckButton } from "../hooks/useCheckButton";
import { useClipButton } from "../hooks/useClipButton";
import { useColorButton } from "../hooks/useColorButton";
import { useContextMenuButton } from "../hooks/useContextMenuButton";
import { useDeleteButton } from "../hooks/useDeleteButton";
import { useDocumentModeButton } from "../hooks/useDocumentModeButton";
import { useDocumentPreviewPageButton } from "../hooks/useDocumentPreviewPageButton";
import { useDownloadDocumentButton } from "../hooks/useDownloadDocumentButton";
import { useFilterButton } from "../hooks/useFilterButton";
import { useFrameAspectRatioButton } from "../hooks/useFrameAspectRatioButton";
import { useHideButton } from "../hooks/useHideButton";
import { useLineTypeButton } from "../hooks/useLineTypeButton";
import { useLinkButton } from "../hooks/useLinkButton";
import { useLockButton } from "../hooks/useLockButton";
import { useMediaButton } from "../hooks/useMediaButton";
import { useMergeButton } from "../hooks/useMergeButton";
import { useMindMapDirectionButton } from "../hooks/useMindMapDirectionButton";
import { usePresentationCreateButton } from "../hooks/usePresentationCreateButton";
import { useShapeButton } from "../hooks/useShapeButton";
import { useStickerColorButton } from "../hooks/useStickerColorButton";
import { useTextAlignButton } from "../hooks/useTextAlignButton";
import { useTextBulletedButton } from "../hooks/useTextBulletedButton";
import { useTextFormatButton } from "../hooks/useTextFormatButton";
import { useUnmergeButton } from "../hooks/useUnmergeButton";
import { createMenuOptionComponent } from "./createMenuOptionComponent";

export const MENU_OPTION_COMPONENTS: Partial<Record<WidgetOptions, FC<MenuOptionComponentProps>>> =
    {
        color: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useColorButton({ name: "color", setActiveSubMenu }),
        ),

        textColor: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useColorButton({ name: "textColor", setActiveSubMenu }),
        ),

        stickerColor: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useStickerColorButton({ setActiveSubMenu }),
        ),

        textBulleted: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useTextBulletedButton({ setActiveSubMenu }),
        ),

        textAlign: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useTextAlignButton({ activeSubMenu, setActiveSubMenu }),
        ),

        link: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useLinkButton({ name: "link", setActiveSubMenu }),
        ),

        linkTo: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useLinkButton({ name: "linkTo", setActiveSubMenu }),
        ),

        delete: createMenuOptionComponent(() => useDeleteButton()),

        lock: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useLockButton({ setActiveSubMenu }),
        ),

        hide: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useHideButton({ setActiveSubMenu }),
        ),

        filter: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useFilterButton({ setActiveSubMenu }),
        ),

        align: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useAlignButton({ activeSubMenu, setActiveSubMenu }),
        ),

        shape: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useShapeButton({ activeSubMenu, setActiveSubMenu }),
        ),

        border: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useBorderButton({ activeSubMenu, setActiveSubMenu }),
        ),

        arrowEnd: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useArrowEndButton({ name: "arrowEnd", activeSubMenu, setActiveSubMenu }),
        ),

        arrowEndFirst: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useArrowEndButton({
                name: "arrowEndFirst",
                activeSubMenu,
                setActiveSubMenu,
            }),
        ),

        lineType: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useLineTypeButton({ activeSubMenu, setActiveSubMenu }),
        ),

        addText: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useAddTextButton({ setActiveSubMenu }),
        ),

        documentPreviewPage: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useDocumentPreviewPageButton({ activeSubMenu, setActiveSubMenu }),
        ),

        documentDownload: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useDownloadDocumentButton({ setActiveSubMenu }),
        ),

        documentMode: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useDocumentModeButton({ setActiveSubMenu }),
        ),

        mindMapDirection: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useMindMapDirectionButton({ activeSubMenu, setActiveSubMenu }),
        ),

        media: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useMediaButton({ setActiveSubMenu }),
        ),

        clip: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useClipButton({ setActiveSubMenu }),
        ),

        check: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useCheckButton({ setActiveSubMenu }),
        ),

        merge: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useMergeButton({ setActiveSubMenu }),
        ),

        unmerge: createMenuOptionComponent(({ setActiveSubMenu }) =>
            useUnmergeButton({ setActiveSubMenu }),
        ),

        aiAssistant: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useAiAssistantButton({ activeSubMenu, setActiveSubMenu }),
        ),

        contextMenu: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useContextMenuButton({ activeSubMenu, setActiveSubMenu }),
        ),

        textFormat: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useTextFormatButton({ activeSubMenu, setActiveSubMenu }),
        ),

        presentationCreate: createMenuOptionComponent(({ setActiveSubMenu }) =>
            usePresentationCreateButton({ setActiveSubMenu }),
        ),

        frameAspectRatio: createMenuOptionComponent(({ activeSubMenu, setActiveSubMenu }) =>
            useFrameAspectRatioButton({ activeSubMenu, setActiveSubMenu }),
        ),

        presentationPlay: createMenuOptionComponent(({ setActiveSubMenu }) =>
            usePresentationPlayButton({ setActiveSubMenu }),
        ),
    };
